import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { SERVICES } from "@/content/services";
import Seo from "@/components/Seo";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";

export default function CareersPage() {
  const [form, setForm] = useState({ name: "", email: "", disciplines: [], portfolio: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggleDiscipline = (title) =>
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(title) ? f.disciplines.filter((d) => d !== title) : [...f.disciplines, title],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.disciplines.length === 0) {
      toast.error("Select at least one discipline above");
      return;
    }
    setSending(true);
    try {
      await api.post("/careers/apply", form);
      setDone(true);
      toast.success("Application registered — welcome to the talent pool");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="careers-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="Careers — Join the Studio" description="Work with Specpixel Entertainment in Melbourne, Australia. Open positions across art, animation, UE5 programming and production for our games and client projects." path="/careers" keywords="game jobs Melbourne, UE5 jobs Australia, game studio careers, Specpixel careers" />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Freelance Registry</p>
          <h1 data-testid="careers-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Careers
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            We work with freelance developers and artists around the world. Register your discipline and portfolio —
            when a project matches your skillset, our producers reach out. Remote-first, paid per milestone.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14 w-full grid grid-cols-1 lg:grid-cols-2 gap-10">
        <motion.section data-testid="careers-disciplines" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h2 className="font-sans font-semibold uppercase tracking-[0.3em] text-lg text-zinc-100 mb-6">Open Disciplines <span className="text-nc-orange text-sm tracking-normal">— select all that apply</span></h2>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                data-testid={`discipline-${s.id}`}
                onClick={() => toggleDiscipline(s.title)}
                className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors duration-300 ${
                  form.disciplines.includes(s.title)
                    ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10"
                    : "text-zinc-400 border-white/10 hover:border-white/30 hover:text-zinc-100"
                }`}
              >
                {form.disciplines.includes(s.title) ? "✓ " : ""}{s.title}
              </button>
            ))}
          </div>
          <div className="mt-8 border border-white/10 bg-black/40 p-6 flex flex-col gap-3">
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-nc-blue">How it works</h3>
            {[
              "01 // Register with your discipline and portfolio",
              "02 // We review every application by hand",
              "03 // Matching projects trigger a direct outreach",
              "04 // Work remotely under NDA, paid per milestone",
            ].map((step) => (
              <p key={step} className="font-mono text-[11px] tracking-[0.15em] text-zinc-400 uppercase">{step}</p>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          {done ? (
            <div data-testid="careers-success" className="border border-nc-green/40 bg-nc-green/5 p-8 h-full flex flex-col items-start justify-center gap-4">
              <UserPlus className="w-8 h-8 text-nc-green" />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Registration received</h2>
              <p className="font-mono text-xs text-zinc-400 leading-relaxed">
                You're in the registry. When a project matches your discipline, we'll reach out directly at {form.email}.
              </p>
            </div>
          ) : (
            <form data-testid="careers-form" onSubmit={handleSubmit} className="border border-white/10 bg-black/40 p-6 flex flex-col gap-4">
              <h2 className="font-sans font-semibold uppercase tracking-[0.3em] text-lg text-zinc-100 mb-1">Register</h2>
              <div>
                <label htmlFor="career-name" className={labelCls}>Full Name / Alias</label>
                <input id="career-name" data-testid="careers-name-input" required minLength={2} className={inputCls} value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="career-email" className={labelCls}>Email</label>
                <input id="career-email" data-testid="careers-email-input" type="email" required className={inputCls} value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={labelCls}>Selected disciplines ({form.disciplines.length})</label>
                <div data-testid="careers-selected-disciplines" className="border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-300 min-h-[42px]">
                  {form.disciplines.length > 0 ? form.disciplines.join(" + ") : <span className="text-zinc-600">Click discipline cards above — pick one or more</span>}
                </div>
              </div>
              <div>
                <label htmlFor="career-portfolio" className={labelCls}>Portfolio URL (ArtStation, GitHub, reel...)</label>
                <input id="career-portfolio" data-testid="careers-portfolio-input" className={inputCls} value={form.portfolio} onChange={set("portfolio")} placeholder="https://" />
              </div>
              <div>
                <label htmlFor="career-message" className={labelCls}>About you (optional)</label>
                <textarea id="career-message" data-testid="careers-message-input" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} />
              </div>
              <button data-testid="careers-submit-button" type="submit" disabled={sending} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 flex items-center justify-center gap-2 transition-colors duration-300">
                <Send className="w-4 h-4" /> {sending ? "Registering..." : "Join the Registry"}
              </button>
            </form>
          )}
        </motion.section>
      </main>

      <SiteFooter />
    </div>
  );
}
